import pluralize from 'pluralize';
import React from 'react';
import { InView } from 'react-intersection-observer';

import { CommentsContextProvider } from '@contexts/CommentsContext';
import useEventTracking from '@hooks/useEventTracking';
import useScrollToHash from '@hooks/useScrollToHash';
import { ImpressionElement, TrackingLocation, impression } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';

import LazyContent from './LazyContent';

interface CommentsContentProps {
  commentableId: number;
  commentableType: CommentableType;
  commentsCount?: number;
  overrideLazyLoad?: boolean;
}

const COMMENT_ID = 'commentId';

export default function CommentsContent({
  commentableId,
  commentableType,
  commentsCount,
  overrideLazyLoad,
}: CommentsContentProps) {
  const searchParams = new URLSearchParams(window.location.search);
  const searchParamCommentId = searchParams.get(COMMENT_ID);
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.CommentSection,
  });
  const commentsRef = useScrollToHash({ hash: '#comments-section', offset: 100 });

  const handleImpression = (inView) => {
    if (inView && [CommentableType.Article, CommentableType.Briefing].includes(commentableType)) {
      trackEvent(impression({ impressionElement: ImpressionElement.CommentsSection }));
    }
  };

  return (
    <CommentsContextProvider
      rootCommentableId={commentableId}
      rootCommentableType={commentableType}
    >
      <Track>
        <InView onChange={handleImpression} rootMargin="100px" triggerOnce>
          {({ inView, ref }) => (
            <div id="comments-section" ref={ref} className="break-words">
              <div
                ref={commentsRef}
                className="flex items-center justify-between border-0 border-b border-solid border-grayscale-gray pb-12"
              >
                <h3 className="m-0 font-suisse-neue text-body-lg font-normal text-primary-black">
                  Conversation
                </h3>
                {commentsCount > 0 && (
                  <span className="font-suisse-intl-mono text-overline-md text-primary-magenta dark:text-primary-pink">
                    {commentsCount} {pluralize('comment', commentsCount)}
                  </span>
                )}
              </div>
              {(inView || searchParamCommentId || overrideLazyLoad) && (
                <LazyContent
                  commentId={searchParamCommentId}
                  commentableId={commentableId}
                  commentableType={commentableType}
                />
              )}
            </div>
          )}
        </InView>
      </Track>
    </CommentsContextProvider>
  );
}
